import { useState, useEffect } from "react";
import { fetchSubTaskedTools } from "../api/api";
import SearchBar from "../components/SearchBar";
import ToolCard from "../components/ToolCard";
import StickyNavbar from "../components/StickyNavbar";
import CustomLoader from '../components/CustomLoader';
import SubtaskSection from '../components/SubtaskSection';
import { useAuth } from '../utils/AuthContext';
import HistorySidebar from '../components/HistorySidebar';
import ToolsModal from '../components/ToolsModal';
import { useHistory } from '../hooks/useHistory';
import { SidebarOpen, SidebarClose, ChevronLeft, ChevronRight } from 'lucide-react';

import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const TulySearch = () => {
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [texting, setTexting] = useState(false);
  const [initialQuery, setInitialQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Use our custom history hook
  const { historyList, isLoading: historyLoading, refreshHistory } = useHistory();
  
  const { token } = useAuth();
  const location = useLocation();
  const { searchQuery } = location.state || {}; // Get passed value

  const handleSearch = async (query) => {
    try {
      setTexting(true);
      setLoading(true);
      setSearchResults(null);
      
      // Use the optimized search that returns either simple or complex results
      const results = await fetchSubTaskedTools(query, token);
      
      console.log("Search results:", results);
      setSearchResults(results);
      
      // Refresh history after search
      refreshHistory();
      
    } catch (error) {
      console.error('Error performing search:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Handle initial search query if provided
    if(searchQuery != null){
      setInitialQuery(searchQuery);
      handleSearch(searchQuery);
    }
  }, []);

  const handleHistoryItemClick = (query) => {
    setInitialQuery(query);
    handleSearch(query);
    
    // On mobile, close the sidebar after selection
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  const openToolsModal = (history) => {
    setSelectedHistory(history);
    setIsModalOpen(true);
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';
  };

  const closeToolsModal = () => {
    setIsModalOpen(false);
    // Re-enable body scrolling
    document.body.style.overflow = 'auto';
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  const simpleResultsVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20
      }
    }
  };

  const toggleVariants = {
    open: { x: 0 },
    closed: { x: 0 },
    hover: { scale: 1.05, transition: { duration: 0.2 } }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <StickyNavbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      
      <div className="flex flex-1 pt-16">

        {/* History Sidebar Component */}
        <AnimatePresence>
          <motion.div
            initial={{ x: sidebarOpen ? 0 : -320 }}
            animate={{ x: sidebarOpen ? 0 : -320 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative"
          >
            <HistorySidebar 
              isOpen={sidebarOpen}
              historyList={historyList}
              isLoading={historyLoading}
              searchTerm={searchTerm}
              onSearchTermChange={setSearchTerm}
              onHistoryItemClick={handleHistoryItemClick}
              onViewToolsClick={openToolsModal}
              onRefreshHistory={() => refreshHistory(true)}
            />
          </motion.div>
        </AnimatePresence>
        
        {/* Main Content */}
        <div className={`flex-1 flex flex-col items-center px-6 transition-all duration-300`}>

          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
            <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
          </div>
          
          <motion.div 
            className="w-full max-w-4xl mb-16 pt-4"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}>
            <AnimatePresence>
              {!texting && (
                <motion.h1 
                  className="text-3xl font-bold mb-6 text-center"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}>
                  What do you want to do?
                </motion.h1>
              )}
            </AnimatePresence>
            
            <div className="flex justify-center items-center">
              <AnimatePresence>
                {loading && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}>
                    <CustomLoader />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Display results based on whether they're complex or simple */}
            <AnimatePresence mode="wait">
              {searchResults && !loading && (
                <motion.div 
                  className="mt-6"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {searchResults.isComplex ? (
                    // Complex results with subtasks
                    <motion.div
                      className="space-y-8"
                      variants={containerVariants}>
                      {(searchResults?.results || []).map((result, index) => (
                        <motion.div
                          key={index}
                          variants={cardVariants}
                          custom={index}>
                          <SubtaskSection result={result} index={index} />
                        </motion.div>
                      ))}
                    </motion.div>
                  ) : (
                    // Simple results (original behavior)
                    <motion.div 
                      className="grid grid-cols-1 md:grid-cols-3 gap-6"
                      variants={simpleResultsVariants}>
                      {searchResults.tools.map((tool, index) => (
                        <motion.div
                          key={tool._id}
                          variants={cardVariants}
                          custom={index}>
                          <ToolCard tool={tool} />
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
      
      {/* Footer with Search Bar */}
      <motion.div 
        className="fixed bottom-0 left-0 w-full bg-transparent backdrop-blur-md py-4 px-6 flex justify-center z-50"
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ 
          type: "spring",
          stiffness: 260,
          damping: 20,
          delay: 0.3
        }}>
        <SearchBar onSearch={handleSearch} initialQuery={initialQuery} />
      </motion.div>

      {/* Tools Modal Component */}
      <ToolsModal 
        isOpen={isModalOpen}
        history={selectedHistory}
        onClose={closeToolsModal}
        onSearchAgain={handleHistoryItemClick}
      />

      {/* Add custom scrollbar style */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default TulySearch;